import type { Control } from "react-hook-form";

import ComboBox from "~/components/formElements/ComboBox";
import DatePickerInput from "~/components/formElements/DatePickerInput.tsx";
import FormSection from "~/components/formElements/FormSection";
import Input from "~/components/formElements/Input.tsx";
import Switch from "~/components/formElements/Switch/Switch.tsx";
import useUserSelectOptions, { allActiveUsersFilterFn } from "~/hooks/form/formOptionsData/useUsersSelectOptions.ts";

// The core fields that every quota form needs at the top. The form owns the
// state; this section only renders the inputs. `title` is required. `usableFrom`
// and `usableUntil` are optional dates. Only the update form has `preselectForTimeTracking`.
export interface QuotaCoreDetailsFormData {
	title: string;
	managedBy?: string | null;
	clientContactPerson?: string | null;
	usableFrom?: Date | null;
	usableUntil?: Date | null;
	preselectForTimeTracking?: boolean;
}

type Props<T extends QuotaCoreDetailsFormData> = {
	control: Control<T>;
	// The user id that `managedBy` holds now. An update form passes it so the option
	// stays selectable after the user goes inactive. A create form omits it.
	ignoreUserId?: string;
	// The update form shows the time tracking preselection. The create forms omit it.
	showPreselectForTimeTracking?: boolean;
};

const QuotaCoreDetailsFormSection = <T extends QuotaCoreDetailsFormData>({
	control,
	ignoreUserId,
	showPreselectForTimeTracking = false,
}: Props<T>) => {
	const userSelectOptions = useUserSelectOptions({
		filterFunction: allActiveUsersFilterFn,
		ignoreIds: ignoreUserId ? [ignoreUserId] : [],
	});

	return (
		<FormSection title="Kontingentdetails">
			<div className="space-y-4">
				<Input name="title"
					   control={control}
					   placeholder="Titel / Beschreibung"
					   label="Titel / Beschreibung" />
				<div className="grid grid-cols-2 gap-x-4">
					<ComboBox allowNew={false}
							  name="managedBy"
							  label="Verantwortlich"
							  control={control}
							  optionsData={userSelectOptions} />
					<Input name="clientContactPerson"
						   control={control}
						   placeholder="Auftraggeber des Kontingents"
						   label="Kontaktperson Kunde" />
				</div>
				<div className="grid grid-cols-2 gap-x-4">
					<DatePickerInput name="usableFrom"
									 control={control}
									 label="Nutzbar ab (optional)" />
					<DatePickerInput name="usableUntil"
									 control={control}
									 label="Nutzbar bis (optional)" />
				</div>
				{showPreselectForTimeTracking &&
					<Switch name="preselectForTimeTracking"
							control={control}
							label="Vorausgewählt im Erfassungsformular" />}
			</div>
		</FormSection>
	);
};

export default QuotaCoreDetailsFormSection;
